import { Box, Flex, Text } from "@nulogy/components";
import CollapsibleSection from "components/common/CollapsibleSection";
import HalfField from "components/common/HalfField";
import Section from "components/common/Section";
import DatePicker from "components/controls/DatePicker";
import Form from "components/controls/Form";
import Input from "components/controls/Input";
import LockToggle from "components/controls/LockToggle";
import Textarea from "components/controls/Textarea";
import {
  formatDateTime,
  formatDuration,
  formatEfficiency,
  formatLabor,
  formatPerformance,
  formatProductionRate,
  formatTeardownTimeInputInHours,
  NO_DATA_LABEL,
} from "components/helpers/format";
import PackmanagerJobLink from "components/pages/schedule/PackmanagerJobLink";
import PackmanagerWorkOrderLink from "components/pages/schedule/PackmanagerWorkOrderLink";
import ProductionDetailsSummary from "components/pages/schedule/workBlocks/ProductionDetailsSummary";
import WorkBlock from "domain/WorkBlock";
import useApplicationContext from "hooks/apollo/applicationContext/useApplicationContext";
import React from "react";
import styled from "styled-components";
import DateTime from "utils/DateTime";
import Num from "utils/Num";
import ProductionDetail from "./ProductionDetail";

const Divider = styled(Box)(({ theme }) => ({
  borderTop: `1px solid ${theme.colors.grey}`,
}));

export default function EditWorkBlockModalView(props) {
  const {
    onClose,
    blockData,
    onSave,
    setErrors,
    selectedWorkOrder,
    selectedLine,
    effectiveProductionRateFor,
    expectedQuantityFor,
    workBlockLayout,
    workBlockModalUIState,
  } = props;

  const applicationContext = useApplicationContext();
  const { block } = blockData;
  const initialWorkOrder = block.workOrder;
  const initialValues = {
    ...block,
    efficiencyOverride: Num.decimalToPercent(block.efficiencyOverride) || "",
    endsAt: block.endsAt,
    jobId: block.jobId,
    laborOverride: block.laborOverride || "",
    lockedOnSchedule: block.lockedOnSchedule || false,
    performanceOverride: Num.decimalToPercent(block.performanceOverride) || "",
    productionRateOverride: block.productionRateOverride || "",
    startsAt: block.startsAt,
    teardownTimeDuration: block.teardownTimeDuration,
    teardownTimeDurationHours: formatTeardownTimeInputInHours(block.teardownTimeDuration),
    workOrder: {
      ...initialWorkOrder,
      id: initialWorkOrder?.id,
      notes: initialWorkOrder?.notes,
      productionRateDependsOnNumberOfPeople: initialWorkOrder?.productionRateDependsOnNumberOfPeople,
    },
    workTimeEndsAt: block.workTimeEndsAt,
    workTimeStartsAt: block.workTimeStartsAt,
  };
  const { productionDetailsOpen, toggleProductionDetails } = workBlockModalUIState;

  const validate = (data) => {
    const errors = WorkBlock.validateInput(data);

    setErrors(errors);

    return errors;
  };

  const handleSave = (data) =>
    new Promise(() => {
      onSave({
        ...data,
        __typename: "WorkBlock",
        efficiencyOverride: Num.percentToDecimal(data.efficiencyOverride) || null,
        laborOverride: data.laborOverride || null,
        performanceOverride: Num.percentToDecimal(data.performanceOverride) || null,
        productionRateOverride: data.productionRateOverride || null,
      });
      onClose();
    });

  const onTeardownTimeChange = ({ value, setFieldValue }) => {
    setFieldValue("teardownTimeDuration", DateTime.hoursToSeconds(value));
  };

  const lineEfficiency = WorkBlock.lineEfficiency({ ...block, line: selectedLine });

  const productionRateDependsOnNumberOfPeople =
    initialValues.productionRateDependsOnNumberOfPeople || selectedWorkOrder?.productionRateDependsOnNumberOfPeople;
  const standardPeople = initialValues.standardPeople || selectedWorkOrder?.standardPeople;
  const formattedStandardPeople = formatLabor(standardPeople);
  const formattedLineEfficiency = formatEfficiency(lineEfficiency, { suffix: false });
  const description = WorkBlock.description({ workOrder: selectedWorkOrder }, workBlockLayout);
  const descriptionHeader = WorkBlock.LAYOUTS.find((layout) => layout.value === workBlockLayout).label;

  let { overlappingPlannedBreaks } = initialValues;

  const handleFormChange = (e) => {
    if (e.values) {
      overlappingPlannedBreaks = WorkBlock.overlappingPlannedBreaks(e.values);
    }
  };

  return (
    <Form
      id="editWorkBlock"
      initialValues={initialValues}
      onChange={handleFormChange}
      onSave={handleSave}
      validate={validate}
    >
      {({ values: workBlock }) => (
        <>
          <Section controls={<LockToggle field="lockedOnSchedule" />} title="Scheduling details">
            <Flex columnGap="x2" flexWrap="wrap" rowGap="x2">
              <HalfField
                className="spec-selected-work-order-description"
                data-testid="work-order-description"
                labelText={descriptionHeader}
                mb="0"
              >
                {description ?? NO_DATA_LABEL}
              </HalfField>
              <HalfField
                className="spec-selected-work-order-code"
                data-testid="work-order-code"
                labelText="Work order code"
                mb="0"
              >
                <Box display="inline">
                  <PackmanagerWorkOrderLink
                    code={selectedWorkOrder.code}
                    externalUuid={selectedWorkOrder.externalUuid}
                  />
                </Box>
              </HalfField>
              {block.jobId && (
                <HalfField data-testid="published-job" labelText="Job" mb="0">
                  <PackmanagerJobLink applicationContext={applicationContext} jobId={block.jobId} />
                </HalfField>
              )}
              <HalfField className="spec-selected-line" labelText="Line" mb="0">
                {selectedLine.name}
              </HalfField>
              {overlappingPlannedBreaks.length > 0 && (
                <HalfField className="spec-selected-planned-breaks" labelText="Planned breaks" mb="0">
                  {overlappingPlannedBreaks.map(({ plannedBreakName, duration }) => (
                    <Text key={plannedBreakName}>{`${plannedBreakName}: ${formatDuration(duration)}`}</Text>
                  ))}
                </HalfField>
              )}
              <Flex className="customDatePicker" columnGap="x2" rowGap="x2" width="100%">
                <HalfField>
                  <DatePicker
                    className="spec-work-time-starts-at"
                    disabled={workBlock.lockedOnSchedule}
                    field="workTimeStartsAt"
                    labelText="Work starts"
                  />
                </HalfField>
                <HalfField>
                  <DatePicker
                    className="spec-work-time-ends-at"
                    disabled={workBlock.lockedOnSchedule}
                    field="workTimeEndsAt"
                    labelText="Work ends"
                  />
                </HalfField>
              </Flex>
            </Flex>
          </Section>
          <Section className="spec-teardown-time-section" data-testid="teardown-time-section" title="Teardown time">
            <Flex columnGap="x2" flexWrap="wrap" rowGap="x2">
              <HalfField mb="0">
                <Input
                  data-testid="teardown-time-label"
                  disabled={workBlock.lockedOnSchedule}
                  field="teardownTimeLabel"
                  id="teardown-time-label"
                  labelText="Label"
                />
              </HalfField>
              <HalfField mb="0">
                <Input
                  className="spec-teardown-time-duration"
                  data-testid="teardown-time-duration"
                  disabled={workBlock.lockedOnSchedule}
                  field="teardownTimeDurationHours"
                  inputWidth="100px"
                  labelText="Duration (h)"
                  onChange={onTeardownTimeChange}
                  step="any"
                  type="number"
                />
              </HalfField>
              {workBlock.teardownTimeDurationHours > 0 && (
                <>
                  <HalfField labelText="Teardown starts" mb="0">
                    {formatDateTime(workBlock.workTimeEndsAt)}
                  </HalfField>
                  <HalfField labelText="Teardown ends" mb="0">
                    {formatDateTime(
                      DateTime.addDuration(workBlock.workTimeEndsAt, workBlock.teardownTimeDuration, "seconds"),
                    )}
                  </HalfField>
                </>
              )}
            </Flex>
          </Section>
          <CollapsibleSection
            isOpen={productionDetailsOpen}
            onToggle={toggleProductionDetails}
            title="Production details"
          >
            <ProductionDetail
              disabled={workBlock.lockedOnSchedule}
              field="productionRateOverride"
              label="Standard production rate"
              overrideValue={workBlock.productionRateOverride}
              specClassName="spec-base-production-rate"
              value={formatProductionRate({
                productionRate: workBlock.standardProductionRate,
                unitOfMeasure: selectedWorkOrder.unitOfMeasure,
              })}
            />
            <ProductionDetail
              disabled={workBlock.lockedOnSchedule}
              field="performanceOverride"
              label="Performance"
              overrideValue={workBlock.performanceOverride}
              specClassName="spec-performance"
              value={formatPerformance(workBlock.performance)}
            />
            <ProductionDetail
              additionalInfo={
                !productionRateDependsOnNumberOfPeople && (
                  <span>
                    Overriding labor will <strong>not</strong> affect production rate
                  </span>
                )
              }
              data-testid="standard-people-detail"
              disabled={workBlock.lockedOnSchedule}
              field="laborOverride"
              label="Standard people"
              overrideValue={workBlock.laborOverride}
              specClassName="spec-standard-people"
              value={formattedStandardPeople}
            />
            <ProductionDetail
              disabled={workBlock.lockedOnSchedule}
              field="efficiencyOverride"
              label="Line efficiency"
              overrideValue={workBlock.efficiencyOverride}
              specClassName="spec-line-efficiency"
              value={formattedLineEfficiency}
            />
            <Divider width="calc(50% + 8px + 96px)" />
            <ProductionDetailsSummary
              effectiveProductionRate={effectiveProductionRateFor(workBlock)}
              expectedQuantity={expectedQuantityFor(workBlock)}
              unitOfMeasure={selectedWorkOrder.unitOfMeasure}
            />
          </CollapsibleSection>
          <Textarea field="workOrder[notes]" placeholder="Add a work order note..." />
        </>
      )}
    </Form>
  );
}
